/**
 * DeveloperRegistrationErrorPage - Registration Error Page
 *
 * Simple, clean layout matching DeveloperWelcomePage design:
 * - Centered card with max-w-2xl width
 * - White background for page, gray-50 for card
 * - Footer outside card
 * - Uses ECGridColors for styling
 * - Minimal content: error icon + title + 2-3 paragraphs + 2 buttons
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { AlertCircle, Headphones, RefreshCw } from 'lucide-react';
import { AnonymousPortalLayout } from '@/components/common/AnonymousPortalLayout';
import { ECGridColors } from '@/types/colors';

// ============================================================================
// Props Interface
// ============================================================================

interface DeveloperRegistrationErrorPageProps {
  errorMessage?: string;
  dataErrorMessage?: string; // Alternative prop name from data-error-message attribute
}

// ============================================================================
// Main Component
// ============================================================================

export const DeveloperRegistrationErrorPage = React.forwardRef<
  HTMLDivElement,
  DeveloperRegistrationErrorPageProps
>(({ errorMessage, dataErrorMessage }, ref) => {
  const { t } = useTranslation(NS.DEVELOPER_REGISTRATION);
  // Support both prop naming conventions
  const displayErrorMessage = errorMessage || dataErrorMessage;

  return (
    <AnonymousPortalLayout ref={ref}>
      <div className="w-full max-w-2xl space-y-6">
          {/* Error Card - Simple and Clean */}
          <div className="bg-gray-50 rounded-lg shadow-md border border-gray-200 p-6 sm:p-8">
            {/* Error Icon (center) */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
            </div>

            {/* Title + Subtitle (center) */}
            <div className="text-center mb-8">
              <h1
                className="text-2xl sm:text-3xl font-bold mb-2"
                style={{ color: ECGridColors.tealDark }}
              >
                {t('DeveloperRegistrationError_ErrorTitle', 'Something Went Wrong')}
              </h1>
              <p className="text-lg text-gray-600">
                {t(
                  'DeveloperRegistrationError_Subtitle',
                  'We were unable to complete your registration.'
                )}
              </p>
            </div>

            {/* Specific Error Message (conditional, amber box) */}
            {displayErrorMessage && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800 text-left">{displayErrorMessage}</p>
                </div>
              </div>
            )}

            {/* Content (2-3 paragraphs, left-aligned) */}
            <div className="space-y-4 text-gray-700 text-sm sm:text-base mb-8">
              <p>
                {t(
                  'DeveloperRegistrationError_Paragraph1',
                  'The issue may be temporary. Please try registering again.'
                )}
              </p>

              <p>
                {t(
                  'DeveloperRegistrationError_Paragraph2',
                  'If you completed payment, check your email for confirmation from Stripe. Your payment may have been processed successfully.'
                )}
              </p>

              <p>
                {t(
                  'DeveloperRegistrationError_Paragraph3',
                  'If the problem persists, contact our support team at'
                )}{' '}
                <a
                  href="mailto:support@ecgrid.com"
                  className="font-medium hover:underline"
                  style={{ color: ECGridColors.blue }}
                >
                  support@ecgrid.com
                </a>
                .
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <a
                href="/developer-registration"
                className="inline-flex items-center justify-center gap-2 w-full py-3.5 px-8 bg-primary text-primary-foreground font-medium rounded-lg
                  transition-all duration-200 hover:bg-primary/90 active:scale-[0.99]
                  focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <RefreshCw className="h-4 w-4" />
                {t('DeveloperRegistrationError_TryAgainButton', 'Try Again')}
              </a>
              <a
                href="mailto:support@ecgrid.com"
                className="inline-flex items-center justify-center gap-2 w-full py-3.5 px-8 bg-transparent border border-primary text-primary font-medium rounded-lg
                  transition-all duration-200 hover:bg-primary/5 active:scale-[0.99]
                  focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Headphones className="h-4 w-4" />
                {t('DeveloperRegistrationError_ContactButton', 'Contact Support')}
              </a>
            </div>
          </div>

      </div>
    </AnonymousPortalLayout>
  );
});

DeveloperRegistrationErrorPage.displayName = 'DeveloperRegistrationErrorPage';
